import { SignUpErrors, SignUpValues } from 'voyager';

import { TranslationKey, TranslationsFunc } from '@lang';
import { FormSubmissionError } from '@store/errors';
import { isIntegrationUnavailable } from '@utils/integrationErrorCode';

export type SignUpErrorKeys = {
  spaMemberNumber?: TranslationKey;
  form?: TranslationKey;
};

export type SignUpPayload = {
  email: string;
  alreadySpaMember: 'yes' | 'no';
  externalSpaId?: string;
};

const SPA_MEMBER_NUMBER_PATTERN = /^\d+$/;

const SPA_MEMBERSHIP_ERROR_KEYS: Record<string, TranslationKey> = {
  spa_id_invalid: 'error.spa_id_invalid',
  spa_id_and_email_mismatch: 'error.spa_id_and_email_mismatch',
};

const isSpaMemberSignUp = (values: SignUpValues, isSpaIntegrated: boolean) =>
  isSpaIntegrated && values.isSpaMember;

export const validateSignUp = (
  values: SignUpValues,
  isSpaIntegrated: boolean
): SignUpErrorKeys | undefined => {
  if (!isSpaMemberSignUp(values, isSpaIntegrated)) return undefined;

  const spaMemberNumber = values.spaMemberNumber.trim();
  if (!spaMemberNumber) return { spaMemberNumber: 'error.required' };
  if (!SPA_MEMBER_NUMBER_PATTERN.test(spaMemberNumber)) {
    return { spaMemberNumber: 'error.spa_member_number_invalid' };
  }

  return undefined;
};

export const buildSignUpPayload = (
  values: SignUpValues,
  isSpaIntegrated: boolean
): SignUpPayload => {
  const email = values.email.trim();
  return isSpaMemberSignUp(values, isSpaIntegrated)
    ? { email, alreadySpaMember: 'yes', externalSpaId: values.spaMemberNumber.trim() }
    : { email, alreadySpaMember: 'no' };
};

const firstViolationCode = (violations: unknown): string | undefined => {
  if (!Array.isArray(violations)) return undefined;
  const code = (violations[0] as { code?: unknown } | null | undefined)?.code;
  return typeof code === 'string' ? code : undefined;
};

export const mapSignUpError = (error: unknown): SignUpErrorKeys => {
  if (!(error instanceof FormSubmissionError)) return { form: 'error.registrationFailed' };

  if (isIntegrationUnavailable({ response: { data: error.errors } })) {
    return { form: 'error.spa_membership_unavailable' };
  }

  const spaCode = firstViolationCode(error.errors.externalSpaId);
  const spaErrorKey = spaCode ? SPA_MEMBERSHIP_ERROR_KEYS[spaCode] : undefined;
  if (spaErrorKey) return { spaMemberNumber: spaErrorKey };

  return { form: 'error.registrationFailed' };
};

export const translateSignUpErrors = (
  errorKeys: SignUpErrorKeys,
  t: TranslationsFunc
): SignUpErrors => ({
  spaMemberNumber: errorKeys.spaMemberNumber && t(errorKeys.spaMemberNumber),
  form: errorKeys.form && t(errorKeys.form),
});
